import type { PosterElement, PosterTextElement } from "@studio/shared";
import { cssWeight, posterFont } from "./fonts";

/** 1pt = 25.4/72 mm */
export const MM_PER_PT = 25.4 / 72;

/** 큰 글씨는 줄 간격을 좁게(목업과 같은 규칙). */
export function lineHeightFactor(sizePt: number): number {
  return sizePt > 24 ? 1.1 : 1.5;
}

export type TextMeasure = (text: string, fontCss: string) => number;

/** 측정용 CSS font 문자열 — 100px 로 재고 비례로 줄인다(작은 글씨 반올림 오차를 피한다). */
export function measureFontCss(element: Pick<PosterTextElement, "font" | "weight">): string {
  const font = posterFont(element.font);
  return `${cssWeight(font, element.weight)} 100px "${font.family}"`;
}

export interface TextLayout {
  lines: string[];
  /** mm */
  lineHeight: number;
  /** mm */
  height: number;
  /** 줄마다 폭(mm) — 가운데·오른쪽 정렬에 쓴다 */
  widths: number[];
}

/**
 * 글상자 줄바꿈(D4) — 편집 화면·PNG·PDF 가 **이 결과 하나**를 같이 쓴다.
 * 🔑 브라우저의 자동 줄바꿈을 믿지 않는다: 화면과 PDF 가 다르게 끊기면 인쇄물이 깨진다.
 *    학생이 넣은 줄바꿈은 그대로 두고, 상자 폭을 넘는 줄만 «글자 단위» 로 나눈다
 *    (한글은 어절보다 글자 단위가 자연스럽고, 영어 단어가 길면 그래도 넘치지 않는다).
 *    단, 공백에서 끊을 수 있으면 공백에서 끊는다.
 */
export function layoutText(element: PosterTextElement, measure: TextMeasure): TextLayout {
  const sizeMm = element.size * MM_PER_PT;
  const scale = sizeMm / 100;
  const fontCss = measureFontCss(element);
  const widthOf = (text: string) => measure(text, fontCss) * scale;
  const lines: string[] = [];
  for (const paragraph of element.text.split("\n")) {
    if (paragraph === "") {
      lines.push("");
      continue;
    }
    let rest = paragraph;
    while (rest.length > 0) {
      if (widthOf(rest) <= element.w + 0.01) {
        lines.push(rest);
        break;
      }
      const chars = [...rest];
      let fit = 1;
      while (fit < chars.length && widthOf(chars.slice(0, fit + 1).join("")) <= element.w + 0.01) fit += 1;
      let cut = fit;
      const lastSpace = chars.slice(0, fit + 1).lastIndexOf(" ");
      if (lastSpace > 0 && fit < chars.length) cut = lastSpace + 1;
      lines.push(chars.slice(0, cut).join("").replace(/\s+$/u, ""));
      rest = chars.slice(cut).join("").replace(/^\s+/u, "");
    }
  }
  const lineHeight = sizeMm * lineHeightFactor(element.size);
  return {
    lines,
    lineHeight,
    height: Math.max(1, lines.length) * lineHeight,
    widths: lines.map((line) => widthOf(line)),
  };
}

let canvas: HTMLCanvasElement | null = null;
/** 브라우저 canvas 로 잰다(글꼴이 불러와진 뒤). */
export const canvasMeasure: TextMeasure = (text, fontCss) => {
  canvas ??= document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return text.length * 60;
  context.font = fontCss;
  return context.measureText(text).width;
};

/** 요소의 높이(mm). 글상자는 줄 수로 정해진다. */
export function elementHeight(element: PosterElement, measure: TextMeasure): number {
  return element.type === "text" ? layoutText(element, measure).height : element.h;
}

/** QR 최소 인쇄 크기 3cm(입력 문서 2단계) */
export const QR_MIN_MM = 30;
